import React from 'react';

type FormatComponent = React.ComponentType<{ children: React.ReactNode }>;

interface ParseOptions {
  bold?: FormatComponent;
  italic?: FormatComponent;
}

// ** must be before *; lookahead/lookbehind on * prevents matching inside **
const PATTERN = /\*\*(.*?)\*\*|(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g;

/**
 * Parses **text** and *text* markers into bold/italic React elements.
 * Renders <strong> for bold and <em> for italic by default.
 * Unclosed or unmatched markers are treated as plain text.
 */
export function parseInlineMarkdown(text: string, options: ParseOptions = {}): React.ReactNode {
  if (!text) return text;
  const Bold = (options.bold ?? 'strong') as FormatComponent;
  const Italic = (options.italic ?? 'em') as FormatComponent;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of text.matchAll(PATTERN)) {
    if (match.index === undefined) continue;
    const matchIndex = match.index;
    if (matchIndex > lastIndex) nodes.push(text.slice(lastIndex, matchIndex));

    if (match[1] !== undefined) {
      nodes.push(<Bold key={key++}>{match[1]}</Bold>);
    } else {
      nodes.push(<Italic key={key++}>{match[2]}</Italic>);
    }
    lastIndex = matchIndex + match[0].length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes.length === 1 && typeof nodes[0] === 'string' ? nodes[0] : nodes;
}
